type LoginCredentials = {
  fullName: string;
  martialId: string;
  password: string;
  websiteUrl?: string;
  // Logged-in bureau's saved Website Links, appended after the message.
  websiteLinks?: unknown;
};

export type WebsiteLink = {
  heading: string;
  url: string;
};

// Accepts the stored array (or its JSON string) and keeps complete entries.
export const parseWebsiteLinks = (value: unknown): WebsiteLink[] => {
  let list = value;
  if (typeof value === "string") {
    try {
      list = value.trim() ? JSON.parse(value) : [];
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .map((entry) => ({
      heading: String(entry?.heading ?? "").trim(),
      url: String(entry?.url ?? "").trim(),
    }))
    .filter((entry) => entry.heading && entry.url);
};

// Appends the "Important Links" section; unchanged message when no links.
export const appendWebsiteLinks = (message: string, websiteLinks: unknown): string => {
  const links = parseWebsiteLinks(websiteLinks);
  if (links.length === 0) return message;

  const section = [
    "━━━━━━━━━━━━━━━━",
    "",
    "🔗 Important Links",
    ...links.flatMap((link) => ["", `🌐 ${link.heading}:`, link.url]),
  ].join("\n");

  return `${message}\n\n${section}`;
};

export const getLoginWebsiteLinks = (websiteUrl: string | undefined, websiteLinks: unknown): WebsiteLink[] => {
  const configuredWebsite = websiteUrl?.trim();
  const website = configuredWebsite
    ? /^https?:\/\//i.test(configuredWebsite) ? configuredWebsite : `https://${configuredWebsite}`
    : "https://meemarriage.com/login";
  const links = parseWebsiteLinks(websiteLinks);
  const isWebsite = (link: WebsiteLink) => link.url.replace(/\/+$/, "") === website.replace(/\/+$/, "");
  return [
    { heading: links.find(isWebsite)?.heading || "Website", url: website },
    ...links.filter((link) => !isWebsite(link)),
  ];
};

export const buildLoginCredentialsMessage = ({
  fullName,
  martialId,
  password,
  websiteUrl,
  websiteLinks,
}: LoginCredentials): string => {
  if (!martialId || !password) {
    throw new Error("Login ID or password is missing for this profile. Please reload the profile and try again.");
  }

  const message = [
    `Hello! ${fullName || ""}`.trim(),
    "",
    "Here are your login credentials:",
    "",
    `Marital ID: ${martialId}`,
    `Password: ${password}`,
  ].join("\n");

  return appendWebsiteLinks(message, getLoginWebsiteLinks(websiteUrl, websiteLinks));
};
